import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <BrandLogo size={28} />
            <span className="font-bold text-lg">PassADC</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Forgot your password?</h1>
          <p className="text-muted-foreground text-sm">
            Enter the email on your account and we&apos;ll send you a reset link.
          </p>
        </div>

        {sent ? (
          <div
            data-testid="forgot-sent-card"
            className="glass rounded-2xl p-6 md:p-8 border border-border text-center space-y-3"
          >
            <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
            <h2 className="text-lg font-semibold">Check your inbox</h2>
            <p className="text-sm text-muted-foreground">
              If <strong>{email}</strong> is registered, a reset link is on its way. It expires in 1 hour. Don&apos;t see it? Check your spam folder.
            </p>
            <Link
              to="/login"
              data-testid="forgot-back-to-login"
              className="inline-block text-primary text-sm font-medium hover:underline pt-2"
            >
              Back to sign in
            </Link>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            data-testid="forgot-form"
            className="glass rounded-2xl p-6 md:p-8 border border-border space-y-5"
          >
            {error && (
              <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-lg p-3" data-testid="forgot-error">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="fp-email" className="block text-sm font-medium mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  id="fp-email"
                  data-testid="forgot-email-input"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-card border border-border text-sm focus:outline-none focus:border-primary/40"
                />
              </div>
            </div>

            <Button
              data-testid="forgot-submit"
              type="submit"
              disabled={loading || !email}
              className="w-full rounded-full py-6 text-base font-semibold"
            >
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) : "Send reset link"}
            </Button>
          </form>
        )}

        <p className="text-center text-sm mt-6 text-muted-foreground">
          Remembered it?{" "}
          <Link to="/login" className="text-primary font-medium hover:underline" data-testid="forgot-to-login">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
